// src/admin/hooks/useForms.js
import { useState, useEffect, useCallback } from 'react';
import useIsMounted from '../../shared/hooks/useIsMounted';
import { fetchWithAuth } from '../auth/fetchWithAuth';
import buildAdminFormsQuery from './buildAdminFormsQuery';
import { API_URL } from '../../config/env';

export default function useForms(queryString = '') {
  const isMounted = useIsMounted();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchForms = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const apiUrl = `${API_URL}/v1/admin/forms`;
      const qs = buildAdminFormsQuery(queryString);
      const response = await fetchWithAuth(qs ? `${apiUrl}?${qs}` : apiUrl);

      if (!response.ok) {
        throw new Error('Failed to fetch forms');
      }

      const formsData = await response.json();
      setForms(formsData);
    } catch (err) {
      if (!isMounted.current) return;
      console.error('Error fetching forms:', err);
      setError(err.message);
    } finally {
      if (!isMounted.current) return;
      setLoading(false);
    }
  }, [queryString, isMounted]);

  const fetchFormById = useCallback(async (id) => {
    try {
      const apiUrl = `${API_URL}/v1/admin/forms/${id}`;
      const response = await fetchWithAuth(apiUrl);

      if (!response.ok) {
        throw new Error('Failed to fetch form');
      }

      return await response.json();
    } catch (err) {
      if (!isMounted.current) return;
      console.error('Error fetching form by ID:', err);
      throw err;
    }
  }, [isMounted]);

  const fetchFormBySlug = useCallback(
    async (slug) => {
      try {
        const apiUrl = `${API_URL}/v1/admin/forms?slug=${encodeURIComponent(slug)}`;
        const response = await fetchWithAuth(apiUrl);

        if (!response.ok) {
          throw new Error('Failed to fetch form');
        }

        const formsData = await response.json();
        const brief = formsData.find((form) => form.slug === slug);
        if (!brief?.id) {
          return undefined;
        }
        return fetchFormById(brief.id);
      } catch (err) {
        if (!isMounted.current) return;
        console.error('Error fetching form by slug:', err);
        throw err;
      }
    },
    [fetchFormById, isMounted]
  );

  const uploadFormDefinition = useCallback(
    async (formPayload) => {
      const apiUrl = `${API_URL}/v1/admin/forms`;
      const response = await fetchWithAuth(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ form: formPayload }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const msg =
          (Array.isArray(data.errors) && data.errors.join(', ')) || data.error || 'Upload failed';
        throw new Error(msg);
      }
      await fetchForms();
      return data;
    },
    [fetchForms]
  );

  // Fetch forms on mount
  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  return {
    forms,
    loading,
    error,
    fetchForms,
    fetchFormBySlug,
    fetchFormById,
    uploadFormDefinition,
  };
}

